import { colors } from '@/constants';
import styled, { css, keyframes } from 'styled-components';

export const alertTheme = {
  success: {
    emphasis: '#23A047',
    primary: '#DFF8E6',
    secondary: '#EEFFF3',
  },
  information: {
    emphasis: '#0052EA',
    primary: '#E0EDFF',
    secondary: '#F1F6FD',
  },
  warning: {
    emphasis: '#F79009',
    primary: '#FFFAEB',
    secondary: '#FFFCF5',
  },
  danger: {
    emphasis: '#F44336',
    primary: '#FFE4EA',
    secondary: '#FFF6F8',
  },
};
export const alertGray = '#4F4F4F';

type AlertTheme = keyof typeof alertTheme;
type AlertBackgroundType = 'primary' | 'secondary';

const fadeIn = keyframes`
  from {
    opacity: 0;
    top: -50%;
  }
  to {
    opacity: 1;
    top: 20px;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    top: 20px;
  }
  to {
    opacity: 0;
    top: -50%;
  }
`;

export const Content = styled.div<{
  $bordered: boolean;
  $borderLeft: boolean;
  $theme: AlertTheme;
  $bgType: AlertBackgroundType;
  $isVisible?: boolean;
}>`
  padding: 12px;
  position: fixed;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 40px);
  max-width: 460px;
  z-index: 9999999;
  border-radius: 10px;
  animation: ${(i) => (i.$isVisible ? fadeIn : fadeOut)} 0.5s ease-in-out forwards;
  border: ${(i) => (i.$bordered ? '1px solid ' + alertTheme[i.$theme].emphasis : 'none')};
  ${(i) =>
    i.$borderLeft &&
    css`
      border-left: 6px solid ${alertTheme[i.$theme].emphasis};
    `}
  background-color: ${(i) => (i.$bgType === 'primary' ? alertTheme[i.$theme].primary : alertTheme[i.$theme].secondary)};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

export const Row = styled.div<{ $justifyContent?: 'space-between' | 'flex-start' | 'flex-end' | 'center' }>`
  flex-direction: row;
  display: inline-flex;
  gap: 10px;
  width: 100%;
  align-items: center;
  justify-content: ${(i) => i.$justifyContent || 'space-between'};
`;

export const CloseButton = styled.div`
  cursor: pointer;
`;

export const AlertButton = styled.div<{ $theme: AlertTheme }>`
  background-color: ${({ $theme }) => alertTheme[$theme].emphasis};
  padding: 8px 12px;
  border-radius: 6px;
  color: ${colors.brand.white};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

export const AlertButtonText = styled.p`
  font-size: 12px;
  font-weight: 400;
  padding: 0;
  margin: 0;
  color: ${colors.brand.white};
`;

export const AlertTitle = styled.p<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  flex: 1;
  width: 100%;
  margin: 0;
  color: ${(i) => i.color};
`;

export const AlertClose = styled.p<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  padding: 0;
  margin: 0;
  color: ${(i) => i.color};
`;

export const AlertMessage = styled.li`
  font-size: 14px;
  font-weight: 400;
  padding: 0;
  margin: 0;
  color: ${alertGray};
`;

export const AlertMessageList = styled.ul`
  padding: 0 20px;
  margin: 0;
`;

export const AlertMessageNormal = styled.div`
  padding: 0;
  margin: 0;
`;

export const AlertMessageItem = styled.div`
  font-size: 14px;
  font-weight: 400;
  padding: 0;
  margin: 0;
  color: ${alertGray};
`;

export const Separator = styled.div`
  width: 100%;
  height: 1px;
  margin: 12px 0px;
  background-color: ${colors.background1};
`;
